import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ChevronRight } from "lucide-react";

const SITE_URL = "https://hireds.in";

const SECTOR_BLURBS = {
  railway: "RRB NTPC, Group D, ALP, JE, RPF — 1.4 lakh hires each year",
  banking: "IBPS PO, SBI Clerk, RBI Grade B — every major bank exam",
  ssc: "CGL, CHSL, MTS, GD Constable, Stenographer — Group B & C",
  upsc: "Civil Services, NDA, CDS, IFS, ESE — apex Group A recruitments",
  defense: "Army, Navy, Air Force, Coast Guard, CAPF & Agniveer notifications",
  teaching: "CTET, KVS, NVS, DSSSB, State TET & Professor posts",
  "state-govt": "UPPSC, BPSC, MPPSC, RPSC + every State PSC & Subordinate Service",
  psu: "ONGC, NTPC, IOCL, BHEL & every Maharatna / Navratna recruitment",
};

export default function SectorsIndex() {
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    api.get("/sectors").then((r) => setSectors(r.data.sectors || [])).catch(() => setSectors([]));
    const oldTitle = document.title;
    document.title = "Job Sectors — Railway, Banking, SSC, UPSC, Defense | hireds.in";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    const oldDesc = metaDesc.content;
    metaDesc.content = "Browse Indian government jobs by sector. Dedicated hubs for Railway, Banking, SSC, UPSC, Defense, Teaching, State Govt & PSU recruitment 2026.";
    let canonical = document.querySelector('link[rel="canonical"]');
    const oldCanonical = canonical ? canonical.href : null;
    if (canonical) canonical.href = `${SITE_URL}/sectors`;
    return () => {
      document.title = oldTitle;
      if (oldDesc !== undefined) metaDesc.content = oldDesc;
      if (oldCanonical !== null && canonical) canonical.href = oldCanonical;
    };
  }, []);

  return (
    <article className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="sectors-index">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Sectors" }]} />
      <header className="mb-8">
        <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Browse Jobs by Sector
        </h1>
        <p className="mt-3 text-base text-slate-700 leading-relaxed max-w-3xl">
          Every Indian government job notification belongs to a sector. Pick yours below to see open vacancies,
          admit cards, results, prep guides and FAQs — all curated by our editorial team.
        </p>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sectors.map((s) => (
          <Link
            key={s.slug}
            to={`/sectors/${s.slug}`}
            data-testid={`sector-card-${s.slug}`}
            className="block bg-white border border-slate-200 hover:border-orange-400 hover:shadow-md rounded-xl p-5 transition-all"
          >
            <div className="flex items-center justify-between">
              <span className="inline-block text-xs uppercase tracking-wider font-bold text-orange-600 bg-orange-50 border border-orange-200 rounded px-2 py-0.5">
                {s.name}
              </span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
            <div className="text-lg font-extrabold text-slate-900 mt-3 line-clamp-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              {s.h1}
            </div>
            <div className="text-sm text-slate-600 mt-1 line-clamp-2">{SECTOR_BLURBS[s.slug] || s.intro}</div>
          </Link>
        ))}
      </div>
    </article>
  );
}
